import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TenantManagement.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function TenantManagement() {
    const [tenants, setTenants] = useState([]);
    const [editingTenant, setEditingTenant] = useState(null);
    const [expandedTenant, setExpandedTenant] = useState(null);
    const [tenantUsers, setTenantUsers] = useState({});
    const [newName, setNewName] = useState('');
    const [newPublicTitle, setNewPublicTitle] = useState('');
    const [newDomain, setNewDomain] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [addingUserForTenant, setAddingUserForTenant] = useState(null);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserIsSuperuser, setNewUserIsSuperuser] = useState(true);
    const [newUserIsGlobalAdmin, setNewUserIsGlobalAdmin] = useState(false);

    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchTenants = async () => {
        try {
            const response = await axios.get(`${API_BASE}/admin/tenants`, {
                withCredentials: true
            });
            setTenants(response.data);
        } catch (error) {
            console.error('Error fetching tenants:', error);
            if (error.response?.status === 403) {
                setError('Global admin access required');
            } else {
                setError('Failed to load tenants');
            }
        }
    };

    const fetchTenantUsers = async (tenantId) => {
        try {
            const response = await axios.get(`${API_BASE}/admin/tenants/${tenantId}/users`, {
                withCredentials: true
            });
            setTenantUsers(prev => ({ ...prev, [tenantId]: response.data }));
        } catch (error) {
            console.error('Error fetching tenant users:', error);
        }
    };

    const handleToggleExpand = (tenantId) => {
        if (expandedTenant === tenantId) {
            setExpandedTenant(null);
        } else {
            setExpandedTenant(tenantId);
            if (!tenantUsers[tenantId]) {
                fetchTenantUsers(tenantId);
            }
        }
    };

    const handleCreateTenant = async (e) => {
        e.preventDefault();
        if (!newName.trim()) {
            setError('Tenant name is required');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.post(`${API_BASE}/admin/tenants`, {
                name: newName,
                publicTitle: newPublicTitle || undefined,
                domain: newDomain || undefined
            }, {
                withCredentials: true
            });

            setNewName('');
            setNewPublicTitle('');
            setNewDomain('');
            setSuccess('Tenant created successfully');
            fetchTenants();
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create tenant');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTenant = async (tenantId) => {
        if (!editingTenant.name.trim()) {
            setError('Tenant name is required');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.put(`${API_BASE}/admin/tenants/${tenantId}`, {
                name: editingTenant.name,
                publicTitle: editingTenant.public_title,
                domain: editingTenant.domain
            }, {
                withCredentials: true
            });

            setEditingTenant(null);
            setSuccess('Tenant updated successfully');
            fetchTenants();
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update tenant');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTenant = async (tenantId, tenantName) => {
        if (!window.confirm(`Are you sure you want to delete "${tenantName}"? This will permanently delete ALL users, photos, and albums for this tenant.`)) {
            return;
        }

        if (!window.confirm(`FINAL WARNING: This action cannot be undone. Type the tenant name to confirm would not be enough — are you absolutely sure?`)) {
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.delete(`${API_BASE}/admin/tenants/${tenantId}`, {
                withCredentials: true
            });

            setSuccess(`Tenant "${tenantName}" deleted successfully`);
            fetchTenants();
            if (expandedTenant === tenantId) {
                setExpandedTenant(null);
            }
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete tenant');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleGlobalAdmin = async (userId, currentStatus) => {
        try {
            await axios.put(`${API_BASE}/admin/users/${userId}/global-admin`, {
                isGlobalAdmin: !currentStatus
            }, {
                withCredentials: true
            });

            // Refresh the users for the expanded tenant
            if (expandedTenant) {
                fetchTenantUsers(expandedTenant);
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update global admin status');
        }
    };

    const handleAddUser = async (e, tenantId) => {
        e.preventDefault();

        if (!newUserEmail.trim()) {
            setError('Email is required');
            return;
        }
        if (!newUserUsername.trim()) {
            setError('Username is required');
            return;
        }
        if (!newUserPassword || newUserPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.post(`${API_BASE}/admin/tenants/${tenantId}/users`, {
                email: newUserEmail,
                username: newUserUsername,
                password: newUserPassword,
                isSuperuser: newUserIsSuperuser,
                isGlobalAdmin: newUserIsGlobalAdmin
            }, {
                withCredentials: true
            });

            // Reset form
            setNewUserEmail('');
            setNewUserUsername('');
            setNewUserPassword('');
            setNewUserIsSuperuser(true);
            setNewUserIsGlobalAdmin(false);
            setAddingUserForTenant(null);

            setSuccess('User created successfully');
            fetchTenantUsers(tenantId);
            fetchTenants(); // Refresh to update user count
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create user');
        } finally {
            setLoading(false);
        }
    };

    const cancelAddUser = () => {
        setAddingUserForTenant(null);
        setNewUserEmail('');
        setNewUserUsername('');
        setNewUserPassword('');
        setNewUserIsSuperuser(true);
        setNewUserIsGlobalAdmin(false);
        setError('');
    };

    return (
        <div className="tenant-management-container">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="create-tenant-section">
                <h3>Create New Tenant</h3>
                <form onSubmit={handleCreateTenant}>
                    <input
                        type="text"
                        placeholder="Tenant Name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        disabled={loading}
                    />
                    <input
                        type="text"
                        placeholder="Public Gallery Title (optional)"
                        value={newPublicTitle}
                        onChange={(e) => setNewPublicTitle(e.target.value)}
                        disabled={loading}
                    />
                    <input
                        type="text"
                        placeholder="Domain (optional)"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        disabled={loading}
                    />
                    <button type="submit" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Tenant'}
                    </button>
                </form>
            </div>

            <div className="tenants-list-section">
                <h3>Tenants ({tenants.length})</h3>
                <div className="tenants-list">
                    {tenants.map(tenant => (
                        <div key={tenant.id} className={`tenant-item ${expandedTenant === tenant.id ? 'expanded' : ''}`}>
                            {editingTenant?.id === tenant.id ? (
                                <div className="tenant-edit-form">
                                    <input
                                        type="text"
                                        placeholder="Tenant Name"
                                        value={editingTenant.name}
                                        onChange={(e) => setEditingTenant({...editingTenant, name: e.target.value})}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Public Gallery Title"
                                        value={editingTenant.public_title || ''}
                                        onChange={(e) => setEditingTenant({...editingTenant, public_title: e.target.value})}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Domain"
                                        value={editingTenant.domain || ''}
                                        onChange={(e) => setEditingTenant({...editingTenant, domain: e.target.value})}
                                    />
                                    <div className="tenant-edit-actions">
                                        <button onClick={() => handleUpdateTenant(tenant.id)}>Save</button>
                                        <button onClick={() => setEditingTenant(null)}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="tenant-header" onClick={() => handleToggleExpand(tenant.id)}>
                                        <div className="tenant-info">
                                            <h4>{tenant.name}</h4>
                                            {tenant.public_title && (
                                                <p className="tenant-title">{tenant.public_title}</p>
                                            )}
                                            {tenant.domain && (
                                                <p className="tenant-domain">{tenant.domain}</p>
                                            )}
                                            <div className="tenant-stats">
                                                <span>{tenant.user_count} users</span>
                                                <span>{tenant.photo_count} photos</span>
                                                <span>{tenant.album_count} albums</span>
                                            </div>
                                        </div>
                                        <div className="tenant-actions" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => setEditingTenant({...tenant})}>Edit</button>
                                            <button
                                                onClick={() => handleDeleteTenant(tenant.id, tenant.name)}
                                                className="delete-btn"
                                            >
                                                Delete
                                            </button>
                                            <span className="expand-arrow">{expandedTenant === tenant.id ? '\u25B2' : '\u25BC'}</span>
                                        </div>
                                    </div>

                                    {expandedTenant === tenant.id && (
                                        <div className="tenant-users-section">
                                            <div className="tenant-users-header">
                                                <h5>Users</h5>
                                                {addingUserForTenant !== tenant.id && (
                                                    <button
                                                        className="add-user-btn"
                                                        onClick={() => setAddingUserForTenant(tenant.id)}
                                                    >
                                                        Add User
                                                    </button>
                                                )}
                                            </div>

                                            {addingUserForTenant === tenant.id && (
                                                <form className="add-user-form" onSubmit={(e) => handleAddUser(e, tenant.id)}>
                                                    <input
                                                        type="email"
                                                        placeholder="Email"
                                                        value={newUserEmail}
                                                        onChange={(e) => setNewUserEmail(e.target.value)}
                                                        disabled={loading}
                                                        required
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Username"
                                                        value={newUserUsername}
                                                        onChange={(e) => setNewUserUsername(e.target.value)}
                                                        disabled={loading}
                                                        required
                                                    />
                                                    <input
                                                        type="password"
                                                        placeholder="Password (min 6 characters)"
                                                        value={newUserPassword}
                                                        onChange={(e) => setNewUserPassword(e.target.value)}
                                                        disabled={loading}
                                                        required
                                                        minLength={6}
                                                    />
                                                    <div className="add-user-checkboxes">
                                                        <label className="checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={newUserIsSuperuser}
                                                                onChange={(e) => setNewUserIsSuperuser(e.target.checked)}
                                                                disabled={loading}
                                                            />
                                                            <span>Superuser</span>
                                                        </label>
                                                        <label className="checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={newUserIsGlobalAdmin}
                                                                onChange={(e) => setNewUserIsGlobalAdmin(e.target.checked)}
                                                                disabled={loading}
                                                            />
                                                            <span>Global Admin</span>
                                                        </label>
                                                    </div>
                                                    <div className="add-user-actions">
                                                        <button type="submit" disabled={loading}>
                                                            {loading ? 'Creating...' : 'Create User'}
                                                        </button>
                                                        <button type="button" onClick={cancelAddUser} disabled={loading}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </form>
                                            )}

                                            {tenantUsers[tenant.id] ? (
                                                tenantUsers[tenant.id].length > 0 ? (
                                                    <div className="tenant-users-list">
                                                        {tenantUsers[tenant.id].map(user => (
                                                            <div key={user.id} className="tenant-user-item">
                                                                <div className="tenant-user-info">
                                                                    <span className="tenant-user-name">{user.username}</span>
                                                                    <span className="tenant-user-email">{user.email}</span>
                                                                    <div className="tenant-user-badges">
                                                                        {user.is_superuser && (
                                                                            <span className="badge superuser-badge">Superuser</span>
                                                                        )}
                                                                        {user.is_global_admin && (
                                                                            <span className="badge global-admin-badge">Global Admin</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="tenant-user-actions">
                                                                    <label className="checkbox-label compact">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={user.is_global_admin || false}
                                                                            onChange={() => handleToggleGlobalAdmin(user.id, user.is_global_admin)}
                                                                        />
                                                                        <span>Global Admin</span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="no-users">No users in this tenant</p>
                                                )
                                            ) : (
                                                <p className="loading-users">Loading users...</p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default TenantManagement;
